import { hashKey, type QueryKey, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useRef, useState } from "react";

import type { ServerSentChange } from "../../hooks/useServerSentEvents";

const UPDATED_MARK_MS = 2000;
const MAX_CHANGED_IDS = 100;

export type LiveRowMark =
  | { kind: "updated" }
  | { kind: "moved"; actorName: string | null; at: number };

export interface LiveChanges<Row> {
  rows: Row[];
  matchingIds: string[];
}

interface ListPage<Row> {
  data: Row[];
  total: number;
}

interface UseLiveListSyncOptions<Row> {
  listKey: QueryKey;
  queryKey: QueryKey;
  getRowId: (row: Row) => string;
  fetchChanges: (ids: string[]) => Promise<LiveChanges<Row>>;
  currentUserId: string | undefined;
}

export default function useLiveListSync<Row>({
  listKey,
  queryKey,
  getRowId,
  fetchChanges,
  currentUserId,
}: UseLiveListSyncOptions<Row>) {
  const queryClient = useQueryClient();
  const [marks, setMarks] = useState<ReadonlyMap<string, LiveRowMark>>(
    new Map(),
  );
  const [pendingIds, setPendingIds] = useState<ReadonlySet<string>>(new Set());
  const timers = useRef(new Set<ReturnType<typeof setTimeout>>());

  const viewHash = hashKey(queryKey);
  const latest = useRef({ viewHash, listKey, queryKey, fetchChanges });
  latest.current = { viewHash, listKey, queryKey, fetchChanges };

  const clear = useCallback(() => {
    setMarks(new Map());
    setPendingIds(new Set());
  }, []);

  const [markedView, setMarkedView] = useState(viewHash);
  if (markedView !== viewHash) {
    setMarkedView(viewHash);
    clear();
  }

  useEffect(() => {
    const pending = timers.current;
    return () => {
      for (const timer of pending) clearTimeout(timer);
    };
  }, []);

  const refetch = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey: latest.current.listKey });
    clear();
  }, [queryClient, clear]);

  const onChange = useCallback(
    async (changes: ServerSentChange[] | null) => {
      if (!changes) {
        await refetch();
        return;
      }
      if (!changes.length) return;
      if (changes.length > MAX_CHANGED_IDS) {
        await refetch();
        return;
      }

      const view = latest.current;
      let result: LiveChanges<Row>;
      try {
        result = await view.fetchChanges(changes.map((c) => c.id));
      } catch {
        await refetch();
        return;
      }
      if (latest.current.viewHash !== view.viewHash) return;

      const page = queryClient.getQueryData<ListPage<Row>>(view.queryKey);
      const onScreen = new Set(page?.data.map(getRowId));
      const matching = new Set(result.matchingIds);
      const fresh = new Map(result.rows.map((row) => [getRowId(row), row]));

      const updated: string[] = [];
      const moved = new Map<string, LiveRowMark>();
      const removed = new Set<string>();
      const counted = new Set<string>();

      for (const { id, actorId, actorName } of changes) {
        const isOwn = !!currentUserId && actorId === currentUserId;
        if (onScreen.has(id) && matching.has(id)) {
          if (!isOwn) updated.push(id);
        } else if (onScreen.has(id) && isOwn) {
          removed.add(id);
        } else if (onScreen.has(id)) {
          moved.set(id, { kind: "moved", actorName, at: Date.now() });
          counted.add(id);
        } else if (matching.has(id) && !isOwn) {
          counted.add(id);
        }
      }

      queryClient.setQueryData<ListPage<Row>>(view.queryKey, (old) =>
        old
          ? {
              ...old,
              data: old.data
                .filter((row) => !removed.has(getRowId(row)))
                .map((row) => fresh.get(getRowId(row)) ?? row),
              total: old.total - removed.size,
            }
          : old,
      );

      setMarks((prev) => {
        const next = new Map(prev);
        for (const { id } of changes) next.delete(id);
        for (const id of updated) next.set(id, { kind: "updated" });
        for (const [id, mark] of moved) next.set(id, mark);
        return next;
      });
      setPendingIds((prev) => {
        const next = new Set(prev);
        for (const { id } of changes) {
          if (counted.has(id)) next.add(id);
          else next.delete(id);
        }
        return next;
      });

      if (updated.length) {
        const timer = setTimeout(() => {
          timers.current.delete(timer);
          setMarks((prev) => {
            const next = new Map(prev);
            for (const id of updated) {
              if (next.get(id)?.kind === "updated") next.delete(id);
            }
            return next;
          });
        }, UPDATED_MARK_MS);
        timers.current.add(timer);
      }
    },
    [queryClient, getRowId, currentUserId, refetch],
  );

  return {
    marks,
    pendingCount: pendingIds.size,
    refresh: refetch,
    onChange,
  };
}
